import { useState, useEffect, useContext } from "react";
import { useMsal } from "@azure/msal-react";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { NotificationsService } from "../../services/NotificationsService";
import { AlertContext, ActiveUserContext } from "../../components/Dashboard/Dashboard";
import { securityCheck } from "../../utils/authHelpers";

export default function useNotificationsReceiver(userPermissions) {
  const { instance } = useMsal();
  const [connection, setConnection] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [newOfferNotifications, setNewOfferNotifications] = useState([]);
  const [continuationToken, setContinuationToken] = useState(null);
  const [newItem, setNewItem] = useState({});
  const [newOfferItem, setNewOfferItem] = useState({});
  const showCompleted = false;
  const activeUser = useContext(ActiveUserContext);
  const { warning } = useContext(AlertContext);

  const getNotifications = (initialLoad) => {
    let token = null;
    if (!initialLoad) {
      token = continuationToken;
    }

    if (token || initialLoad && !isLoading) {
      setIsLoading(true);

      NotificationsService.GetOfferNotifications(instance, activeUser.userIdMcld, showCompleted, token).then(res => {
        setContinuationToken(res.continuationToken);
        setNotifications(prevNotifications => [...prevNotifications, ...res.offerNotifications]);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  };

  const getNewOfferNotifications = () => {
    if (!isLoading) {
      setIsLoading(true);

      NotificationsService.GetNewOfferNotifications(instance, activeUser.userIdMcld).then(res => {
        setNewOfferNotifications(res.offerNotifications.filter(({ isRead }) => !isRead));
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  };

  useEffect(() => {
    if (!connection && activeUser.userIdMcld) {
      const newConnection = new HubConnectionBuilder()
        .withUrl("/CarrierOffersNotifications")
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Information)
        .build();

      newConnection.onreconnected(() => {
        newConnection.invoke("AddToGroup", activeUser.userIdMcld);
        newConnection.invoke("AddToGroup", "IncomingNewOfferNotification");
      });

      setConnection(newConnection);
    }
  }, [activeUser]);

  useEffect(() => {
    if (activeUser.userIdMcld && !notifications.length) {
      getNotifications(true);
    }

    if (activeUser.userIdMcld) {
      getNewOfferNotifications();
    }
  }, [activeUser]);

  useEffect(() => {
    async function start() {
      try {
        if (connection.state === "Disconnected") {
          await connection.start();
        }

        await connection.invoke("AddToGroup", activeUser.userIdMcld);

        await connection.invoke("AddToGroup", "IncomingNewOfferNotification");

        connection.on("IncomingOfferNotification", update => {
          setNewItem(update);
        });

        connection.on("IncomingNewOfferNotification", update => {
          const isAdminUser = securityCheck(userPermissions, "CarrierOffersDashboardManager", "Read");
          if (isAdminUser) {
            setNewOfferItem(update);
          }
        });
      } catch (err) {
        console.error(err);
      }
    }

    if (connection && activeUser.userIdMcld) {
      start();
    }
  }, [connection, activeUser]);

  useEffect(() => {
    if (newItem.id) {
      let updatedNotifications = [...notifications];
      const index = updatedNotifications.findIndex(c => c.id == newItem.id);

      if (index >= 0) {
        updatedNotifications[index] = newItem;
      } else {
        updatedNotifications.unshift(newItem);
      }

      if (!showCompleted) {
        updatedNotifications = updatedNotifications.filter(n => !n.actionCompleted);
      }

      setNotifications(updatedNotifications);
    }
  }, [newItem]);

  useEffect(() => {
    if (newOfferItem.orderId) {
      let updatedNotifications = [...newOfferNotifications];

      if (!newOfferItem.isRead) {
        updatedNotifications.unshift(newOfferItem);
      } else {
        updatedNotifications = updatedNotifications.filter(c => c.orderId !== newOfferItem.orderId);
      }

      setNewOfferNotifications(updatedNotifications);
    }
  }, [newOfferItem]);

  return {
    notifications,
    setNotifications,
    getNotifications,
    continuationToken,
    newOfferNotifications,
    getNewOfferNotifications,
  };
}